import { Component } from 'react';

/**
 * Catches render-time crashes so a thrown error shows something useful instead
 * of a white screen.
 *
 * Why this exists: a family reported "the dashboard is simply a blank screen"
 * while every other page worked. With no boundary anywhere in the app, a single
 * TypeError during render unmounts the whole tree and paints nothing — the
 * parent sees white, and we get no information at all unless they know how to
 * open devtools. The error text is shown here deliberately, so a screenshot is
 * enough to diagnose the fault.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Keep the console trace intact for anyone who does open devtools.
    console.error('Uncaught render error:', error, info?.componentStack);
  }

  handleCopy = () => {
    const { error, info } = this.state;
    const text = [
      `Page: ${window.location.pathname}`,
      `Error: ${error?.message || String(error)}`,
      error?.stack || '',
      info?.componentStack || '',
    ].join('\n');
    navigator.clipboard?.writeText(text).then(
      () => this.setState({ copied: true }),
      () => {},
    );
  };

  render() {
    const { error, info, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-xl w-full bg-white rounded-3xl shadow-sm border border-sand-darker p-8">
          <h1 className="font-heading text-2xl font-bold text-fern-dark">This page didn't load properly</h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Something went wrong while drawing this page. Your work is safe — nothing has been lost.
            Try reloading, and if it keeps happening, send us the details below and we'll fix it.
          </p>

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              onClick={() => window.location.reload()}
              className="bg-fern hover:bg-fern-dark text-white rounded-xl px-5 py-2.5 text-sm font-semibold"
              data-testid="error-reload"
            >
              Reload the page
            </button>
            <a
              href="/dashboard"
              className="rounded-xl px-5 py-2.5 text-sm font-semibold border border-fern/25 text-fern hover:bg-fern/5"
            >
              Back to Dashboard
            </a>
            <a
              href="/contact"
              className="rounded-xl px-5 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              Contact us
            </a>
          </div>

          <details className="mt-7 group">
            <summary className="text-sm font-semibold text-slate-500 cursor-pointer select-none">
              Technical details
            </summary>
            <div className="mt-3">
              <button
                onClick={this.handleCopy}
                className="text-xs font-semibold text-moana mb-2"
                data-testid="error-copy"
              >
                {copied ? 'Copied ✓' : 'Copy details'}
              </button>
              <pre
                className="text-[11px] leading-relaxed text-slate-600 bg-slate-50 rounded-xl p-3 overflow-auto max-h-64 whitespace-pre-wrap"
                data-testid="error-details"
              >
{`Page: ${window.location.pathname}
Error: ${error?.message || String(error)}
${error?.stack || ''}
${info?.componentStack || ''}`}
              </pre>
            </div>
          </details>
        </div>
      </div>
    );
  }
}
